<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import {
  Magnet,
  RefreshCw,
  Copy,
  Trash2,
  Search,
  AlertTriangle,
  Hash,
} from 'lucide-vue-next'
import Card from '@/components/ui/Card.vue'
import Button from '@/components/ui/Button.vue'
import Modal from '@/components/ui/Modal.vue'
import Empty from '@/components/ui/Empty.vue'
import Spinner from '@/components/ui/Spinner.vue'
import Badge from '@/components/ui/Badge.vue'
import { listTorrents, removeTorrent, type TorrentInfo } from '@/api/torrent'
import { useToast } from '@/composables/useToast'
import { copyToClipboard } from '@/composables/useClipboard'
import { formatBytes } from '@/composables/useFormat'

const { t, locale } = useI18n()
const toast = useToast()
const qc = useQueryClient()

// ----- query -----
const torrentsQuery = useQuery({
  queryKey: ['torrents'],
  queryFn: () => listTorrents(),
  refetchOnWindowFocus: false,
})

const allTorrents = computed<TorrentInfo[]>(() => torrentsQuery.data.value ?? [])

const errorMessage = computed<string>(() => {
  const e = torrentsQuery.error.value as unknown
  if (e instanceof Error) return e.message
  if (e && typeof e === 'object' && 'message' in e) {
    return String((e as { message: unknown }).message)
  }
  return ''
})

// ----- search -----
const search = ref('')

const filteredTorrents = computed<TorrentInfo[]>(() => {
  const q = search.value.trim().toLowerCase()
  const list = [...allTorrents.value]
  // Default sort: createdAt desc (newest first).
  list.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))
  if (!q) return list
  return list.filter((t) => {
    const hash = (t.infoHash || '').toLowerCase()
    const name = (t.name || '').toLowerCase()
    return hash.includes(q) || name.includes(q)
  })
})

function refresh(): void {
  void qc.invalidateQueries({ queryKey: ['torrents'] })
}

// ----- format helpers -----
function formatRatio(ratio: number): string {
  if (!Number.isFinite(ratio) || ratio <= 0) return '0.00'
  if (ratio >= 100) return ratio.toFixed(0)
  return ratio.toFixed(2)
}

function formatDate(ts: number): string {
  if (!Number.isFinite(ts) || ts <= 0) return '—'
  try {
    return new Intl.DateTimeFormat(locale.value, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(ts * 1000))
  } catch {
    return new Date(ts * 1000).toISOString().slice(0, 16).replace('T', ' ')
  }
}

function truncateName(name: string, max = 48): string {
  if (!name) return '—'
  if (name.length <= max) return name
  return `${name.slice(0, Math.max(0, max - 1))}${t('settings.torrent.truncated')}`
}

function shortHash(hash: string): string {
  if (!hash) return '—'
  if (hash.length <= 20) return hash
  return `${hash.slice(0, 8)}…${hash.slice(-8)}`
}

// Status mapping → AFD status string → UI label.
function statusKey(status: string): string {
  switch (status) {
    case 'active':
    case 'downloading':
      return 'active'
    case 'paused':
      return 'paused'
    case 'waiting':
    case 'pending':
      return 'waiting'
    case 'complete':
      return 'complete'
    case 'error':
      return 'error'
    case 'removed':
      return 'removed'
    default:
      return 'unknown'
  }
}

function statusLabelKey(s: string): string {
  return `settings.torrent.status.${statusKey(s)}`
}

function statusVariant(s: string): 'success' | 'warning' | 'danger' | 'info' | 'neutral' {
  switch (statusKey(s)) {
    case 'complete':
      return 'success'
    case 'active':
    case 'downloading':
      return 'info'
    case 'paused':
      return 'warning'
    case 'error':
      return 'danger'
    default:
      return 'neutral'
  }
}

// ----- copy -----
const copyHash = async (hash: string): Promise<void> => {
  if (!hash) return
  const ok = await copyToClipboard(hash)
  if (ok) toast.success(t('settings.torrent.copiedTitle'), t('settings.torrent.copied'))
  else toast.error(t('settings.torrent.copyFailed'))
}

// ----- remove -----
const removeTarget = ref<TorrentInfo | null>(null)

const removeMutation = useMutation({
  mutationFn: (hash: string) => removeTorrent(hash),
  onSuccess: () => {
    toast.success(t('settings.torrent.removeSuccess'))
    removeTarget.value = null
    void qc.invalidateQueries({ queryKey: ['torrents'] })
  },
  onError: () => {
    // The client.ts wrapper already shows a toast on HTTP errors; we set silent
    // on the mutation below to avoid a duplicate, but still surface a local msg.
  },
})

function askRemove(t: TorrentInfo): void {
  removeTarget.value = t
}

function confirmRemove(): void {
  const target = removeTarget.value
  if (!target) return
  removeMutation.mutate(target.infoHash)
}

function cancelRemove(): void {
  if (removeMutation.isPending.value) return
  removeTarget.value = null
}
</script>

<template>
  <div class="space-y-4">
    <!-- Toolbar -->
    <Card padded>
      <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div class="flex items-center gap-2">
          <Magnet :size="16" class="text-fg-muted" />
          <div>
            <h2 class="text-sm font-semibold text-fg">{{ t('settings.torrent.title') }}</h2>
            <p class="text-xs text-fg-muted">{{ t('settings.torrent.subtitle') }}</p>
          </div>
          <Badge variant="neutral" size="sm" class="ml-2">
            {{ t('settings.torrent.total', { n: allTorrents.length }) }}
          </Badge>
        </div>
        <div class="flex flex-1 items-center justify-end gap-3">
          <div class="relative w-full sm:max-w-xs">
            <Search :size="14" class="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-fg-subtle" />
            <input
              v-model="search"
              type="search"
              :placeholder="t('settings.torrent.search')"
              class="w-full rounded-md border border-border bg-bg-surface pl-9 pr-3 text-sm text-fg placeholder:text-fg-subtle focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500 h-9"
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            :loading="torrentsQuery.isFetching.value"
            @click="refresh"
          >
            <template #icon><RefreshCw :size="14" /></template>
            {{ t('settings.torrent.refresh') }}
          </Button>
        </div>
      </div>
    </Card>

    <!-- Loading skeleton (first load) -->
    <div
      v-if="torrentsQuery.isLoading.value"
      class="rounded-lg border border-border bg-bg-surface p-6"
    >
      <div class="flex items-center justify-center py-8">
        <Spinner :size="20" :label="t('common.loading')" />
      </div>
      <div class="mt-4 space-y-2">
        <div class="h-8 w-full animate-pulse rounded bg-bg-elevated" />
        <div class="h-8 w-5/6 animate-pulse rounded bg-bg-elevated" />
        <div class="h-8 w-2/3 animate-pulse rounded bg-bg-elevated" />
      </div>
    </div>

    <!-- Error state -->
    <div
      v-else-if="torrentsQuery.isError.value"
      class="flex items-start gap-3 rounded-lg border border-danger/40 bg-danger/10 p-4 text-sm text-danger"
    >
      <AlertTriangle :size="16" class="mt-0.5 shrink-0" />
      <div>
        <div class="font-medium">{{ t('settings.torrent.loadError') }}</div>
        <div class="mt-1 text-xs text-fg-muted">
          {{ errorMessage || '—' }}
        </div>
        <Button variant="outline" size="sm" class="mt-3" @click="refresh">
          <template #icon><RefreshCw :size="12" /></template>
          {{ t('settings.torrent.refresh') }}
        </Button>
      </div>
    </div>

    <!-- Empty state -->
    <div
      v-else-if="filteredTorrents.length === 0"
      class="rounded-lg border border-border bg-bg-surface"
    >
      <Empty
        :icon="Hash"
        :title="search ? t('settings.torrent.empty') : t('settings.torrent.empty')"
        :description="t('settings.torrent.emptyHint')"
      />
    </div>

    <!-- Table -->
    <div
      v-else
      class="overflow-hidden rounded-lg border border-border bg-bg-surface"
    >
      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-border text-sm">
          <thead class="bg-bg-elevated">
            <tr>
              <th class="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-fg-muted">
                {{ t('settings.torrent.columns.name') }}
              </th>
              <th class="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-fg-muted">
                {{ t('settings.torrent.columns.hash') }}
              </th>
              <th class="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide text-fg-muted">
                {{ t('settings.torrent.columns.size') }}
              </th>
              <th class="hidden px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide text-fg-muted md:table-cell">
                {{ t('settings.torrent.columns.downloaded') }}
              </th>
              <th class="hidden px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide text-fg-muted md:table-cell">
                {{ t('settings.torrent.columns.uploaded') }}
              </th>
              <th class="hidden px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide text-fg-muted lg:table-cell">
                {{ t('settings.torrent.columns.ratio') }}
              </th>
              <th class="hidden px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide text-fg-muted lg:table-cell">
                {{ t('settings.torrent.columns.seeders') }}
              </th>
              <th class="hidden px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide text-fg-muted lg:table-cell">
                {{ t('settings.torrent.columns.leechers') }}
              </th>
              <th class="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-fg-muted">
                {{ t('settings.torrent.columns.status') }}
              </th>
              <th class="hidden px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-fg-muted xl:table-cell">
                {{ t('settings.torrent.columns.createdAt') }}
              </th>
              <th class="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide text-fg-muted">
                {{ t('settings.torrent.columns.actions') }}
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-border">
            <tr
              v-for="row in filteredTorrents"
              :key="row.infoHash"
              class="hover:bg-bg-hover/40"
            >
              <!-- Name -->
              <td class="px-4 py-2 align-top">
                <div
                  class="font-medium text-fg"
                  :title="row.name"
                >
                  {{ truncateName(row.name) }}
                </div>
                <div
                  v-if="row.savePath"
                  class="mt-0.5 truncate font-mono text-xs text-fg-subtle"
                  :title="row.savePath"
                >
                  {{ row.savePath }}
                </div>
              </td>

              <!-- Info hash -->
              <td class="px-4 py-2 align-top">
                <div class="inline-flex items-center gap-1.5">
                  <code
                    class="font-mono text-[12px] text-fg"
                    :title="row.infoHash"
                  >{{ shortHash(row.infoHash) }}</code>
                  <button
                    type="button"
                    class="inline-flex h-6 w-6 items-center justify-center rounded text-fg-muted hover:bg-bg-hover hover:text-fg"
                    :title="t('settings.torrent.copyTitle')"
                    :aria-label="t('settings.torrent.copyTitle')"
                    @click="copyHash(row.infoHash)"
                  >
                    <Copy :size="12" />
                  </button>
                </div>
              </td>

              <!-- Size -->
              <td class="px-4 py-2 text-right font-mono text-[12px] text-fg">
                {{ formatBytes(row.size) }}
              </td>

              <!-- Downloaded -->
              <td class="hidden px-4 py-2 text-right font-mono text-[12px] text-fg-muted md:table-cell">
                {{ formatBytes(row.downloaded) }}
              </td>

              <!-- Uploaded -->
              <td class="hidden px-4 py-2 text-right font-mono text-[12px] text-fg-muted md:table-cell">
                {{ formatBytes(row.uploaded) }}
              </td>

              <!-- Ratio -->
              <td class="hidden px-4 py-2 text-right font-mono text-[12px] text-fg-muted lg:table-cell">
                {{ formatRatio(row.ratio) }}
              </td>

              <!-- Seeders -->
              <td class="hidden px-4 py-2 text-right lg:table-cell">
                <Badge
                  v-if="row.seeders > 0"
                  variant="success"
                  size="sm"
                >
                  {{ row.seeders }}
                </Badge>
                <span v-else class="font-mono text-[12px] text-fg-subtle">—</span>
              </td>

              <!-- Leechers -->
              <td class="hidden px-4 py-2 text-right lg:table-cell">
                <Badge
                  v-if="row.leechers > 0"
                  variant="warning"
                  size="sm"
                >
                  {{ row.leechers }}
                </Badge>
                <span v-else class="font-mono text-[12px] text-fg-subtle">—</span>
              </td>

              <!-- Status -->
              <td class="px-4 py-2 align-top">
                <Badge :variant="statusVariant(row.status)" size="sm" dot>
                  {{ t(statusLabelKey(row.status)) }}
                </Badge>
              </td>

              <!-- Added -->
              <td class="hidden px-4 py-2 font-mono text-xs text-fg-muted xl:table-cell">
                {{ formatDate(row.createdAt) }}
              </td>

              <!-- Actions -->
              <td class="px-4 py-2 text-right">
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label="Remove torrent"
                  @click="askRemove(row)"
                >
                  <template #icon><Trash2 :size="14" /></template>
                </Button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Confirm remove modal -->
    <Modal
      :model-value="removeTarget !== null"
      :title="t('settings.torrent.confirmRemoveTitle')"
      size="md"
      @update:modelValue="cancelRemove"
    >
      <p class="text-sm text-fg-muted">
        {{ t('settings.torrent.confirmRemove', { name: removeTarget?.name ?? '' }) }}
      </p>
      <div v-if="removeTarget" class="mt-3 rounded-md border border-border bg-bg-elevated p-2 font-mono text-xs text-fg-muted break-all">
        {{ removeTarget.infoHash }}
      </div>
      <template #footer>
        <Button variant="ghost" size="sm" :disabled="removeMutation.isPending.value" @click="cancelRemove">
          {{ t('common.cancel') }}
        </Button>
        <Button
          variant="danger"
          size="sm"
          :loading="removeMutation.isPending.value"
          @click="confirmRemove"
        >
          <template #icon><Trash2 :size="14" /></template>
          {{ t('common.delete') }}
        </Button>
      </template>
    </Modal>
  </div>
</template>

<style scoped>
input[type='search'] {
  -webkit-appearance: none;
}
</style>
